import type { IPublicClientApplication } from "@azure/msal-browser";
import { apiDelete, apiFetch, resolveBlobIdUrl } from "./client";
import { createImageUploadFormData } from "./uploadRequest";
import type { Paged } from "../types/api";

type ApiJob = {
  id: string;
  runPodJobId: string;
  status: "IN_QUEUE" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";
  workerId: string | null;
  delayTimeMs: number | null;
  executionTimeMs: number | null;
  errorMessage: string | null;
  inputBlobId: string | null;
  outputBlobId: string | null;
  outputFileName: string | null;
  outputSizeBytes: number | null;
  createdAt: string;
  // Live generation progress — populated only while status is IN_PROGRESS (null otherwise).
  progressStep: number | null;
  progressTotalSteps: number | null;
  progressCode: string | null;
  progressPercent: number | null;
};

export type RunPodJob = ApiJob & {
  inputBlobUrl: string | null;
  outputBlobUrl: string | null;
};

export const isTerminalStatus = (s: RunPodJob["status"]) => s === "COMPLETED" || s === "FAILED" || s === "CANCELLED";

function toJob(raw: ApiJob): RunPodJob {
  return {
    ...raw,
    inputBlobUrl: raw.inputBlobId === null ? null : resolveBlobIdUrl(raw.inputBlobId),
    outputBlobUrl: raw.outputBlobId === null ? null : resolveBlobIdUrl(raw.outputBlobId),
  };
}

export async function submitRunPodJob(msal: IPublicClientApplication, image: File, enhanceBackground: boolean): Promise<RunPodJob> {
  const formData = await createImageUploadFormData(image, { enhanceBackground });
  return toJob(await apiFetch<ApiJob>("/web/runpod/submit", msal, { method: "POST", body: formData }));
}

// jobId is the internal DB Guid (RunPodJob.id) - the gateway routes /web/runpod/{id:guid}
// off the internal id, NOT the RunPod provider string (runPodJobId), which is not a Guid and
// also changes on retry.
export async function getRunPodStatus(msal: IPublicClientApplication, jobId: string, signal?: AbortSignal): Promise<RunPodJob> {
  return toJob(await apiFetch<ApiJob>(`/web/runpod/${jobId}`, msal, { signal }));
}

export async function retryRunPodJob(msal: IPublicClientApplication, jobId: string): Promise<RunPodJob> {
  return toJob(await apiFetch<ApiJob>(`/web/runpod/${jobId}/retry`, msal, { method: "POST" }));
}

export function deleteRunPodJob(msal: IPublicClientApplication, jobId: string): Promise<void> {
  return apiDelete(`/web/runpod/${jobId}`, msal);
}

export async function fetchRunPodJobs(
  msal: IPublicClientApplication,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<Paged<RunPodJob>> {
  const data = await apiFetch<Paged<ApiJob>>(`/web/runpod?page=${page}&pageSize=${pageSize}`, msal, { signal });
  return { ...data, items: data.items.map(toJob) };
}
